import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { 
  FaCheck, 
  FaTimes, 
  FaClipboardCheck, 
  FaUser, 
  FaPaperPlane, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaCommentAlt,
  FaMinus,
  FaSave,
  FaInfoCircle
} from 'react-icons/fa';
import './ChecklistModal.css';

const ChecklistModal = ({ show, onHide, userId, memberIndex = -1, formType }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show && userId) {
      fetchChecklist();
    }
  }, [show, userId, memberIndex, formType]);
  
  // Safety check to prevent null userId errors
  if (show && !userId) {
    return (
      <Modal show={show} onHide={onHide} size="lg" centered className="checklist-modal">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="w-100 text-center">
            <FaExclamationTriangle className="me-2" />
            Checklist Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <Alert variant="danger" className="d-inline-flex align-items-center">
            <FaExclamationTriangle className="me-2" size={24} />
            <div>
              <h5 className="alert-heading mb-1">Cannot Load Checklist</h5>
              <p className="mb-0">Missing required user information. Please try again or contact support.</p>
            </div>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="outline-secondary" onClick={onHide}>
            <FaTimes className="me-1" /> Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const fetchChecklist = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('employeeToken');
      
      // First try to get existing checklist
      let response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists?userId=${userId}&memberIndex=${memberIndex}&formType=${formType}&type=prepare`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.status === 404) {
        // Checklist doesn't exist, create a new one
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            userId,
            memberIndex,
            formType,
            type: 'prepare'
          })
        });
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch or create checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(err.message || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (index, value) => {
    const updatedChecklist = { ...checklist };
    updatedChecklist.items[index].response = value;
    setChecklist(updatedChecklist);
    updateChecklistItem(index, value, updatedChecklist.items[index].remarks);
  };

  const handleRemarksChange = (index, value) => {
    const updatedChecklist = { ...checklist };
    updatedChecklist.items[index].remarks = value;
    setChecklist(updatedChecklist);
  };

  const updateChecklistItem = async (index, response, remarks) => {
    try {
      const token = localStorage.getItem('employeeToken');
      
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/update-item`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          itemIndex: index,
          response,
          remarks
        })
      });
    } catch (err) {
      console.error('Error updating checklist item:', err);
    }
  };

  const handleSubmitChecklist = async () => {
    if (!checklist) return;
    
    // Check if all items have responses (NA is a valid response)
    const hasIncompleteItems = checklist.items.some(item => 
      item.response === undefined || item.response === null || item.response === ''
    );
    
    if (hasIncompleteItems) {
      setError('Please complete all checklist items before submitting');
      return;
    }

    // If any NA items have empty remarks, ask for remarks
    const naItemsWithoutRemarks = checklist.items.some(item => 
      item.response === 'NA' && (!item.remarks || item.remarks.trim() === '')
    );
    
    if (naItemsWithoutRemarks) {
      setError('Please add remarks for items marked as N/A before submitting');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      
      // Fetch employee information from the backend using the token
      const employeeResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!employeeResponse.ok) {
        throw new Error('Failed to fetch employee information');
      }
      
      const employeeData = await employeeResponse.json();
      if (!employeeData || !employeeData.employee || !employeeData.employee._id) {
        throw new Error('Invalid employee data received');
      }
      
      const employeeId = employeeData.employee._id;
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          employeeId: employeeId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      setSuccess('Checklist submitted successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error submitting checklist:', err);
      setError(err.message || 'Failed to submit checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveRemarks = async (index) => {
    if (!checklist || !checklist._id || !checklist.items || !checklist.items[index]) {
      console.error('Cannot save remarks: Checklist data is incomplete');
      return;
    }
    
    try {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        console.error('Cannot save remarks: No authentication token');
        return;
      }
      
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/update-item`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          itemIndex: index,
          response: checklist.items[index].response,
          remarks: checklist.items[index].remarks
        })
      });
    } catch (err) {
      console.error('Error saving remarks:', err);
    }
  };

  const getFormTitle = () => {
    switch (formType) {
      case '1040':
        return '1040 - Individual Tax Return';
      case '1120S':
        return '1120S - S Corporation Tax Return';
      case '1120':
        return '1120 - C Corporation Tax Return';
      case '1065':
        return '1065 - Partnership Tax Return';
      case 'Bookkeeping':
        return 'Bookkeeping';
      default:
        return formType;
    }
  };

  const getStatusBadge = () => {
    if (!checklist?.status) return null;
    
    const statusConfig = {
      draft: { variant: 'warning', text: 'Draft' },
      submitted: { variant: 'primary', text: 'Submitted' },
      completed: { variant: 'success', text: 'Completed' }
    };
    
    const { variant, text } = statusConfig[checklist.status] || { variant: 'secondary', text: 'Unknown' };
    return <Badge bg={variant} className="ms-2">{text}</Badge>;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="checklist-modal">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="w-100">
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex align-items-center">
              <FaClipboardCheck className="me-2" />
              {getFormTitle()} Checklist
              {getStatusBadge()}
            </div>
            <div className="mt-1">
              <Badge bg="light" text="dark" className="text-uppercase">
                <FaUser className="me-1" />
                {memberIndex === -1 ? 'Primary Client' : `Member ${memberIndex + 1}`}
              </Badge>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <h5 className="mt-3">Loading Checklist</h5>
            <p className="text-muted">Please wait while we prepare your checklist items...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="d-flex align-items-center">
            <FaExclamationTriangle className="flex-shrink-0 me-3" size={24} />
            <div>
              <h5 className="alert-heading">Error Loading Checklist</h5>
              <p className="mb-0">{error}</p>
            </div>
          </Alert>
        ) : success ? (
          <Alert variant="success" className="d-flex align-items-center">
            <FaCheckCircle className="flex-shrink-0 me-3" size={24} />
            <div>
              <h5 className="alert-heading">Success!</h5>
              <p className="mb-0">{success}</p>
            </div>
          </Alert>
        ) : checklist ? (
          <>
            <Alert variant="info" className="d-flex align-items-center">
              <div>
                <h6 className="alert-heading mb-2">
                  <FaInfoCircle className="me-2" />
                  Instructions
                </h6>
                <ul className="mb-0 ps-3">
                  <li>Review each item and select <strong>Yes</strong>, <strong>No</strong>, or <strong>N/A</strong></li>
                  <li>Add remarks for any items marked as No or N/A</li>
                  <li>Click the save icon to save your remarks</li>
                </ul>
              </div>
            </Alert>
            
            <div className="checklist-items mt-4">
              {checklist.items.map((item, index) => (
                <div 
                  key={index} 
                  className={`checklist-item p-3 mb-3 rounded-3 ${item.response ? 'completed' : ''}`}
                  style={{
                    borderLeft: `4px solid ${item.response === 'Yes' ? '#198754' : item.response === 'No' ? '#dc3545' : item.response === 'NA' ? '#ffc107' : '#6c757d'}`,
                    backgroundColor: 'var(--bs-light-bg-subtle)'
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-3 d-flex align-items-center">
                        <span className="badge bg-secondary me-2">{index + 1}</span>
                        {item.text}
                      </h6>
                      
                      <div className="btn-group btn-group-sm mb-3" role="group">
                        <button
                          type="button"
                          className={`btn ${item.response === 'Yes' ? 'btn-success' : 'btn-outline-secondary'}`}
                          onClick={() => handleResponseChange(index, 'Yes')}
                          disabled={checklist.status !== 'draft'}
                        >
                          <FaCheck className="me-1" /> Yes
                        </button>
                        <button
                          type="button"
                          className={`btn ${item.response === 'No' ? 'btn-danger' : 'btn-outline-secondary'}`}
                          onClick={() => handleResponseChange(index, 'No')}
                          disabled={checklist.status !== 'draft'}
                        >
                          <FaTimes className="me-1" /> No
                        </button>
                        <button
                          type="button"
                          className={`btn ${item.response === 'NA' ? 'btn-warning' : 'btn-outline-secondary'}`}
                          onClick={() => handleResponseChange(index, 'NA')}
                          disabled={checklist.status !== 'draft'}
                        >
                          <FaMinus className="me-1" /> N/A
                        </button>
                      </div>

                      {(item.response === 'No' || item.response === 'NA' || item.remarks) && (
                        <div className="mt-2">
                          <Form.Group>
                            <Form.Label className="small text-muted d-flex align-items-center">
                              <FaCommentAlt className="me-1" /> Remarks
                            </Form.Label>
                            <div className="input-group">
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={item.remarks || ''}
                                onChange={(e) => handleRemarksChange(index, e.target.value)}
                                onBlur={() => handleSaveRemarks(index)}
                                placeholder="Add remarks (optional)"
                                disabled={checklist.status !== 'draft'}
                              />
                              {checklist.status === 'draft' && (
                                <Button
                                  variant="outline-primary"
                                  onClick={() => handleSaveRemarks(index)}
                                  title="Save remarks"
                                >
                                  <FaSave />
                                </Button>
                              )}
                            </div>
                          </Form.Group>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {checklist.status === 'draft' ? (
              <div className="d-flex justify-content-between mt-4">
                <Button 
                  variant="outline-secondary" 
                  onClick={onHide}
                >
                  <FaTimes className="me-1" /> Save & Close
                </Button>
                
                <Button
                  variant="primary"
                  onClick={handleSubmitChecklist}
                  disabled={submitting || !checklist.items.every(item => 
                    item.response && (item.response !== 'No' && item.response !== 'NA' ? true : item.remarks)
                  )}
                >
                  {submitting ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="me-1" /> Submit Checklist
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Alert variant="info" className="mt-4">
                <div className="d-flex align-items-center">
                  <FaInfoCircle className="me-2" />
                  <span>This checklist has been {checklist.status}. It is now read-only.</span>
                </div>
              </Alert>
            )}
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default ChecklistModal;
